/* eslint-disable max-lines */
import {
  LocationAlarmUpdate,
  ModifyAlarmUpdateEntry,
} from '@hakimo-ui/hakimo/types';
import { SelectMenu } from '@hakimo-ui/hakimo/ui-elements';
import { Alert, Button, Textarea } from '@hakimo-ui/shared/ui-base';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { RefObject } from 'react';
import { EditableTimestamp } from '../../shared/EditableTimestamp';
import { PicturePicker } from '../../sop-workflow/escalate/PicturePicker';
import {
  EditState,
  RESPONDER_OPTIONS,
  ResponderType,
  SITUATION_OPTIONS,
  SUB_CATEGORY_OPTIONS,
  SubCategory,
  UpdateType,
  derivedUpdateType,
} from '../../sop-workflow/util';
import { displayTime } from '../alarm-updates/util';

dayjs.extend(utc);
dayjs.extend(timezone);

type Opt = { value: string; label: string; dotClass?: string };

const TYPE_ITEMS: Opt[] = [...SITUATION_OPTIONS];
const STATUS_ITEMS: Opt[] = [...SUB_CATEGORY_OPTIONS];
const RESPONDER_ITEMS: Opt[] = [...RESPONDER_OPTIONS];

function renderSelect(
  items: Opt[],
  value: string,
  placeholder: string,
  onChange: (v: string) => void,
  withDot = false
) {
  return (
    <SelectMenu
      value={items.find((o) => o.value === value)}
      items={items}
      displayValue={(item) => item?.label ?? placeholder}
      hideCheckIcon
      id={(item) => item.value || `_none_${placeholder}_`}
      onChange={(opt) => onChange(opt.value)}
      renderItem={
        withDot
          ? (item) =>
              item && (
                <span className="flex items-center gap-2">
                  <span
                    className={`h-2 w-2 shrink-0 rounded-full ${
                      item.dotClass ?? ''
                    }`}
                  />
                  <span>{item.label}</span>
                </span>
              )
          : undefined
      }
    />
  );
}

interface Props {
  ev: LocationAlarmUpdate;
  pending: Partial<ModifyAlarmUpdateEntry>;
  editState: EditState;
  timezone?: string;
  fileInputRef: RefObject<HTMLInputElement>;
  onSave: () => void;
  onCancel: () => void;
  patch: (p: Partial<EditState>) => void;
  /** Roomier snapshot thumbnails, for the wide report timeline. */
  large?: boolean;
}

export function DispatchTimelineEntryEdit({
  ev,
  pending: p,
  editState: s,
  timezone: tz,
  fileInputRef,
  onSave,
  onCancel,
  patch,
  large = false,
}: Props) {
  const originalType = derivedUpdateType(ev.update_type as string);

  return (
    <div className="bg-onlight-bg-2 dark:bg-ondark-bg-2 space-y-3 rounded-lg p-3">
      {s.updateType === 'evidence' &&
        s.s3Urls.length + s.pendingFiles.length === 0 && (
          <Alert type="info">
            At least one image is required for evidence.
          </Alert>
        )}
      <div className="flex flex-wrap items-center gap-2">
        <div className="w-36">
          {renderSelect(
            TYPE_ITEMS,
            s.updateType,
            '',
            (v) => patch({ updateType: v as UpdateType }),
            true
          )}
        </div>

        {s.updateType === 'dispatch' && (
          <div className="w-28">
            {renderSelect(STATUS_ITEMS, s.dispatchStatus || '', 'Status', (v) =>
              patch({ dispatchStatus: v as SubCategory | '' })
            )}
          </div>
        )}

        {s.updateType !== 'evidence' && (
          <div className="w-40">
            {renderSelect(
              RESPONDER_ITEMS,
              s.responderType || '',
              'Responder',
              (v) => patch({ responderType: v as ResponderType | '' })
            )}
          </div>
        )}

        <EditableTimestamp
          live={false}
          timezone={tz}
          containerClassName="bg-onlight-bg-1 dark:bg-ondark-bg-1 border-onlight-line-3 dark:border-ondark-line-3 flex h-7 items-center gap-1 rounded-full border px-1.5"
          initialTimestamp={dayjs(s.tsIso ?? displayTime(ev, p)).valueOf()}
          onDatePickerChange={(iso) => patch({ tsIso: iso })}
        />
      </div>

      {(s.updateType !== 'dispatch' ||
        (originalType !== 'dispatch' &&
          s.s3Urls.length + s.pendingBlobUrls.length > 0)) && (
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <span className="text-onlight-text-1 dark:text-ondark-text-1 text-sm font-medium">
              Snapshots
            </span>
            {s.s3Urls.length + s.pendingFiles.length > 0 && (
              <span className="bg-onlight-bg-3 dark:bg-ondark-bg-3 text-onlight-text-1 dark:text-ondark-text-2 rounded-full px-2 py-0.5 text-xs font-medium">
                {s.s3Urls.length + s.pendingFiles.length} Uploaded
              </span>
            )}
          </div>
          <div className="border-onlight-line-3 dark:bg-dark-bg dark:border-dark-border-surface rounded-lg border bg-white pb-2 pt-1">
            <PicturePicker
              badge={null}
              showHeader={false}
              wrap
              large={large}
              readOnly={s.updateType === 'dispatch'}
              allowRemove={s.updateType === 'dispatch'}
              previewList={[...s.s3Urls, ...s.pendingBlobUrls]}
              fileInputRef={fileInputRef}
              previewIdx={s.previewIdx}
              setPreviewIdx={(i) => patch({ previewIdx: i })}
              onFilesSelect={(files) => {
                const blobs = files.map((f) => URL.createObjectURL(f));
                patch({
                  pendingFiles: [...s.pendingFiles, ...files],
                  pendingBlobUrls: [...s.pendingBlobUrls, ...blobs],
                  imagesModified: true,
                });
              }}
              onFileRemove={(i) => {
                const cut = s.s3Urls.length;
                if (i < cut) {
                  patch({
                    s3Urls: s.s3Urls.filter((_, j) => j !== i),
                    imagesModified: true,
                  });
                } else {
                  const fi = i - cut;
                  URL.revokeObjectURL(s.pendingBlobUrls[fi]);
                  patch({
                    pendingFiles: s.pendingFiles.filter((_, j) => j !== fi),
                    pendingBlobUrls: s.pendingBlobUrls.filter(
                      (_, j) => j !== fi
                    ),
                    imagesModified: true,
                  });
                }
              }}
            />
          </div>
        </div>
      )}

      <div className="space-y-1.5">
        <span className="text-onlight-text-1 dark:text-ondark-text-1 text-sm font-medium">
          Comment:
        </span>
        <Textarea
          minRows={2}
          rows={2}
          value={s.comment}
          onChange={(v) => patch({ comment: v })}
        />
      </div>

      <div className="flex items-center gap-3">
        <Button
          variant="primary"
          className="!py-1"
          onClick={onSave}
          disabled={
            s.updateType === 'evidence' &&
            s.s3Urls.length + s.pendingFiles.length === 0
          }
        >
          Save
        </Button>
        <Button className="!py-1" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
